import { Dialog, DialogContent } from "@/components/ui/dialog";
import { TaskForm } from "./TaskForm";
import { useState, useEffect } from "react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { TaskComments } from "./TaskComments";
import { TaskLinks } from "./TaskLinks";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { 
  Edit, 
  User, 
  Calendar, 
  Flag, 
  Clock,
  CheckCircle2,
  AlertCircle,
  Circle,
  Play,
  UserCheck,
  Target
} from "lucide-react";
import { formatDistanceToNow, format } from "date-fns";
import { es } from "date-fns/locale";
import { useOrganization } from "@/contexts/OrganizationContext";

interface TaskDetailsDialogProps {
  task: any;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUpdate?: () => void;
  userRole?: string | null;
  isSharedView?: boolean;
}

const statusConfig = {
  todo: { label: "Por hacer", icon: Circle, color: "bg-gray-500" },
  to_do: { label: "Por hacer", icon: Circle, color: "bg-gray-500" },
  in_progress: { label: "En progreso", icon: Play, color: "bg-blue-500" },
  on_hold: { label: "En pausa", icon: AlertCircle, color: "bg-yellow-500" },
  done: { label: "Completado", icon: CheckCircle2, color: "bg-green-500" },
};

const priorityConfig = {
  low: { label: "Baja", color: "bg-green-100 text-green-800" },
  medium: { label: "Media", color: "bg-yellow-100 text-yellow-800" },
  high: { label: "Alta", color: "bg-red-100 text-red-800" },
};

export const TaskDetailsDialog = ({ 
  task, 
  open, 
  onOpenChange, 
  onUpdate,
  userRole,
  isSharedView = false
}: TaskDetailsDialogProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const { currentOrganization } = useOrganization();

  // Fetch task creator profile
  const { data: taskCreator } = useQuery({
    queryKey: ["task-creator", task?.created_by || task?.created_by_guest],
    queryFn: async () => {
      if (!task) return null;
      
      // If it's a guest-created task, return guest info
      if (task.created_by_guest) {
        return {
          full_name: task.created_by_guest,
          email: task.guest_email || null,
          isGuest: true
        };
      }
      
      // If it's a user-created task, fetch user profile
      if (task.created_by) {
        const { data, error } = await supabase
          .from("profiles")
          .select("id, full_name, email")
          .eq("id", task.created_by)
          .maybeSingle();
        
        if (error) {
          console.error("Error fetching task creator:", error);
          return null;
        }
        
        return { ...data, isGuest: false };
      }
      
      return null;
    },
    enabled: !!task && !!(task.created_by || task.created_by_guest),
  });

  // Fetch associated lead if task has lead_id
  const { data: associatedLead } = useQuery({
    queryKey: ["task-lead", task?.lead_id],
    queryFn: async () => {
      if (!task?.lead_id) return null;
      
      const { data, error } = await supabase
        .from("leads")
        .select("id, name, details, stage")
        .eq("id", task.lead_id)
        .maybeSingle();
      
      if (error) {
        console.error("Error fetching associated lead:", error);
        return null;
      }
      
      return data;
    },
    enabled: !!task?.lead_id,
  });

  // Add debugging
  useEffect(() => {
    console.log("TaskDetailsDialog: Props changed", { 
      hasTask: !!task, 
      taskTitle: task?.title, 
      open, 
      userRole,
      taskCreator,
      isSharedView
    });
  }, [task, open, userRole, taskCreator, isSharedView]);

  const handleOpenChange = (newOpen: boolean) => {
    console.log("TaskDetailsDialog: Open state changing from", open, "to", newOpen);
    
    if (!newOpen) {
      setIsEditing(false);
    }
    onOpenChange(newOpen);
  };

  if (!task) {
    console.log("TaskDetailsDialog: No task provided, showing empty state");
    return (
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent>
          <div className="p-6 text-center">
            <h3 className="text-lg font-semibold">No hay tarea seleccionada</h3>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  console.log("TaskDetailsDialog: Rendering with task:", task.title);

  const canEdit = !isSharedView && (userRole === 'salesman' || task.created_by === task.currentUserId || task.assigned_to === task.currentUserId);
  const status = statusConfig[task.status as keyof typeof statusConfig] || statusConfig.todo;
  const priority = priorityConfig[task.priority as keyof typeof priorityConfig] || priorityConfig.medium;
  const StatusIcon = status.icon;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className={`max-w-4xl ${isEditing ? 'h-[95vh]' : 'h-[90vh]'} p-0 overflow-hidden`}>
        <div className="flex h-full">
          {/* Main Content */}
          <div className="flex-1 flex flex-col min-h-0">
            {isEditing ? (
              <div className="flex-1 overflow-hidden">
                <ScrollArea className="h-full">
                  <div className="p-6">
                    <TaskForm
                      taskId={task.id}
                      initialData={task}
                      onSuccess={() => {
                        console.log("TaskDetailsDialog: Task form success, closing edit mode");
                        setIsEditing(false);
                        onUpdate?.();
                      }}
                      onCancel={() => {
                        console.log("TaskDetailsDialog: Task form cancelled");
                        setIsEditing(false);
                      }}
                    />
                  </div>
                </ScrollArea>
              </div>
            ) : (
              <ScrollArea className="flex-1">
                <div className="p-6">
                  <div className="space-y-6">
                    {/* Header */}
                    <div className="flex items-start justify-between">
                      <div className="flex-1 space-y-3">
                        <h1 className="text-2xl font-bold">{task.title}</h1>
                        
                        <div className="flex items-center gap-3 flex-wrap">
                          <Badge className={`${status.color} text-white`}>
                            <StatusIcon className="h-3 w-3 mr-1" />
                            {status.label}
                          </Badge>
                          
                          {task.priority && (
                            <Badge variant="secondary" className={priority.color}>
                              <Flag className="h-3 w-3 mr-1" />
                              {priority.label}
                            </Badge>
                          )}
                        </div>
                      </div>
                      
                      {canEdit && (
                        <Button
                          variant="outline"
                          onClick={() => {
                            console.log("TaskDetailsDialog: Edit button clicked");
                            setIsEditing(true);
                          }}
                        >
                          <Edit className="h-4 w-4 mr-2" />
                          Editar
                        </Button>
                      )}
                    </div>

                    <Separator />

                    {/* Task Info Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Task Creator */}
                      {taskCreator && (
                        <Card>
                          <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                              <UserCheck className="h-4 w-4 text-muted-foreground" />
                              <div>
                                <p className="text-sm font-medium">Creado por</p>
                                <div className="flex items-center gap-2 mt-1">
                                  <Avatar className="h-6 w-6">
                                    <AvatarFallback className="text-xs">
                                      {taskCreator.full_name?.[0] || taskCreator.email?.[0] || "?"}
                                    </AvatarFallback>
                                  </Avatar>
                                  <span className="text-sm">
                                    {taskCreator.full_name || taskCreator.email || "Usuario"}
                                    {taskCreator.isGuest && " (Invitado)"}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      )}

                      {/* Assignee */}
                      {task.assignee && (
                        <Card>
                          <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                              <User className="h-4 w-4 text-muted-foreground" />
                              <div>
                                <p className="text-sm font-medium">Asignado a</p>
                                <div className="flex items-center gap-2 mt-1">
                                  <Avatar className="h-6 w-6">
                                    <AvatarFallback className="text-xs">
                                      {task.assignee.full_name?.[0] || task.assignee.email?.[0]}
                                    </AvatarFallback>
                                  </Avatar>
                                  <span className="text-sm">
                                    {task.assignee.full_name || task.assignee.email}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      )}

                      {/* Due Date */}
                      {task.due_date && (
                        <Card>
                          <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                              <Calendar className="h-4 w-4 text-muted-foreground" />
                              <div>
                                <p className="text-sm font-medium">Fecha límite</p>
                                <p className="text-sm text-muted-foreground mt-1">
                                  {format(new Date(task.due_date), "d 'de' MMMM, yyyy", { locale: es })}
                                </p>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      )}

                      {/* Start Date */}
                      {task.start_date && (
                        <Card>
                          <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                              <Clock className="h-4 w-4 text-muted-foreground" />
                              <div>
                                <p className="text-sm font-medium">Fecha de inicio</p>
                                <p className="text-sm text-muted-foreground mt-1">
                                  {format(new Date(task.start_date), "d 'de' MMMM, yyyy", { locale: es })}
                                </p>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      )}

                      {/* Associated Lead */}
                      {associatedLead && (
                        <Card>
                          <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                              <Target className="h-4 w-4 text-muted-foreground" />
                              <div>
                                <p className="text-sm font-medium">Lead asociado</p>
                                <div className="mt-1">
                                  <p className="text-sm font-medium">{associatedLead.name}</p>
                                  {associatedLead.stage && (
                                    <Badge variant="outline" className="mt-1 text-xs">
                                      {associatedLead.stage}
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      )}

                      {/* Created */}
                      <Card>
                        <CardContent className="p-4">
                          <div className="flex items-center gap-3">
                            <Clock className="h-4 w-4 text-muted-foreground" />
                            <div>
                              <p className="text-sm font-medium">Creado</p>
                              <p className="text-sm text-muted-foreground mt-1">
                                {formatDistanceToNow(new Date(task.created_at), { 
                                  addSuffix: true, 
                                  locale: es 
                                })}
                              </p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </div>

                    {/* Description */}
                    {task.description && (
                      <Card>
                        <CardHeader>
                          <CardTitle className="text-lg">Descripción</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <p className="text-sm whitespace-pre-wrap text-muted-foreground">
                            {task.description}
                          </p>
                        </CardContent>
                      </Card>
                    )}
                  </div>
                </div>
              </ScrollArea>
            )}
          </div>

          {/* Sidebar */}
          <div className="w-96 border-l bg-muted/20">
            <ScrollArea className="h-full">
              <div className="p-4">
                <Tabs defaultValue="comments" className="w-full">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="comments">Comentarios</TabsTrigger>
                    <TabsTrigger value="links">Enlaces</TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="comments" className="mt-4">
                    <TaskComments
                      taskId={task.id}
                      projectTaskId={task.project_task_id}
                      organizationId={task.organization_id || currentOrganization?.id || ""}
                      userRole={userRole}
                      isSharedView={isSharedView}
                    />
                  </TabsContent>
                  
                  <TabsContent value="links" className="mt-4">
                    <TaskLinks
                      taskId={task.id}
                      projectTaskId={task.project_task_id}
                      organizationId={task.organization_id || currentOrganization?.id || ""}
                      userRole={userRole}
                    />
                  </TabsContent>
                </Tabs>
              </div>
            </ScrollArea>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
